import { ReactNode, useEffect, useRef, useState } from "react";
import {
  Typography,
  Box,
  IconButton,
  styled,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert,
  LinearProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import {
  Videocam,
  VideocamOff,
  CallEnd,
  QrCode2,
  Fullscreen,
  FullscreenExit,
} from "@mui/icons-material";
import { ZegoExpressEngine } from "zego-express-engine-webrtc";
import { getZegoToken } from "../../../services/api/zego.api";
import AudiencePollDisplay from "../../../components/LiveStream/AudiencePollDisplay";
import PLCVVideoPlayer from "../../LiveStream/Heart/PLCVVideoPlayer_updated";

const VideoCallWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  width: 100%;
  transition: all 0.3s ease-in-out;
`;

const MainContainer = styled(Box)`
  display: flex;
  width: 100%;
  height: 100%;
  position: relative;
  width: 100%;
`;

// ─── Equalizer bar colors cycling ───────────────────────────────────────────
const EQ_COLORS = ["#ff6b9d", "#22d3ee", "#a855f7", "#fbbf24"];

function fmtKaraokeTime(s: number) {
  if (!isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

// ─── Floating music note specs (fixed so render is stable) ──────────────────
const FLOAT_NOTES = [
  { note: "♪", left: "8%",  delay: "0s",    dur: "4.2s", size: "1.5rem" },
  { note: "♫", left: "22%", delay: "0.8s",  dur: "5.1s", size: "1.8rem" },
  { note: "♬", left: "38%", delay: "1.6s",  dur: "3.9s", size: "1.3rem" },
  { note: "♩", left: "54%", delay: "0.4s",  dur: "4.7s", size: "1.6rem" },
  { note: "♪", left: "70%", delay: "2.1s",  dur: "5.4s", size: "1.4rem" },
  { note: "♫", left: "84%", delay: "1.1s",  dur: "3.6s", size: "2rem"   },
  { note: "♬", left: "14%", delay: "2.8s",  dur: "4.9s", size: "1.2rem" },
  { note: "♩", left: "62%", delay: "3.3s",  dur: "4.1s", size: "1.7rem" },
];

// ─── EQ bar heights (cycles every 0.3-0.7s per bar) ─────────────────────────
const EQ_BARS = [22, 14, 28, 10, 24, 18, 30, 12, 26, 16, 20, 8];

function KaraokeOverlay({
  trackName,
  currentTime,
  duration,
}: {
  trackName: string;
  currentTime: number;
  duration: number;
}) {
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        overflow: "hidden",
        borderRadius: "inherit",
        zIndex: 8,
      }}
    >
      {/* ── Floating music notes ── */}
      {FLOAT_NOTES.map((n, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: n.left,
            bottom: "18%",
            color: EQ_COLORS[i % 4],
            fontSize: n.size,
            opacity: 0,
            textShadow: `0 0 12px ${EQ_COLORS[i % 4]}`,
            animation: `floatNote ${n.dur} ${n.delay} ease-in infinite`,
            "@keyframes floatNote": {
              "0%":   { opacity: 0,   transform: "translateY(0)   scale(0.8)" },
              "15%":  { opacity: 0.9, transform: "translateY(-40px) scale(1)"  },
              "85%":  { opacity: 0.7, transform: "translateY(-220px) scale(1.1)" },
              "100%": { opacity: 0,   transform: "translateY(-280px) scale(0.7)" },
            },
          }}
        >
          {n.note}
        </Box>
      ))}

      {/* ── Bottom gradient overlay bar ── */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          pt: 5,
          pb: 1.5,
          px: 2,
          background:
            "linear-gradient(to top, rgba(0,0,0,0.97) 0%, rgba(0,0,0,0.82) 55%, transparent 100%)",
        }}
      >
        {/* Row 1: mic + title + EQ bars */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.75 }}>
          {/* Pulsing mic */}
          <Typography
            sx={{
              fontSize: "1.6rem",
              lineHeight: 1,
              animation: "micPulse 1.4s ease-in-out infinite",
              "@keyframes micPulse": {
                "0%,100%": { transform: "scale(1)",    filter: "drop-shadow(0 0 4px #ff6b9d)" },
                "50%":     { transform: "scale(1.18)", filter: "drop-shadow(0 0 12px #ff6b9d)" },
              },
            }}
          >
            🎤
          </Typography>

          {/* KARAOKE MODE label */}
          <Typography
            sx={{
              color: "#ff6b9d",
              fontWeight: 900,
              fontSize: { xs: "0.85rem", sm: "1rem" },
              letterSpacing: 3,
              textTransform: "uppercase",
              textShadow: "0 0 18px rgba(255,107,157,0.9)",
              flexShrink: 0,
            }}
          >
            Karaoke&nbsp;Mode
          </Typography>

          {/* Spacer */}
          <Box sx={{ flex: 1 }} />

          {/* Animated EQ bars */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              gap: "3px",
              height: 32,
            }}
          >
            {EQ_BARS.map((h, i) => (
              <Box
                key={i}
                sx={{
                  width: 4,
                  borderRadius: "2px",
                  bgcolor: EQ_COLORS[i % 4],
                  boxShadow: `0 0 6px ${EQ_COLORS[i % 4]}`,
                  animation: `eqBar ${0.35 + (i % 5) * 0.1}s ${(i * 0.06).toFixed(2)}s ease-in-out infinite alternate`,
                  height: `${h}px`,
                  "@keyframes eqBar": {
                    from: { transform: "scaleY(0.25)", opacity: 0.6 },
                    to:   { transform: "scaleY(1)",    opacity: 1   },
                  },
                  transformOrigin: "bottom",
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Row 2: track name */}
        {trackName && (
          <Typography
            sx={{
              color: "rgba(255,255,255,0.92)",
              fontWeight: 700,
              fontSize: { xs: "0.8rem", sm: "0.95rem" },
              mb: 0.75,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              textShadow: "0 0 8px rgba(34,211,238,0.5)",
            }}
          >
            ♪&nbsp;{trackName}
          </Typography>
        )}

        {/* Row 3: progress bar + time */}
        <Box sx={{ position: "relative" }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 4,
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.15)",
              "& .MuiLinearProgress-bar": {
                borderRadius: 2,
                background: "linear-gradient(90deg, #ff6b9d 0%, #a855f7 50%, #22d3ee 100%)",
                boxShadow: "0 0 8px rgba(168,85,247,0.7)",
              },
            }}
          />
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
            <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.68rem" }}>
              {fmtKaraokeTime(currentTime)}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.68rem" }}>
              {fmtKaraokeTime(duration)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Top-right LIVE pill ── */}
      <Box
        sx={{
          position: "absolute",
          top: 12,
          right: 12,
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          bgcolor: "rgba(0,0,0,0.72)",
          border: "1px solid rgba(255,107,157,0.6)",
          borderRadius: 1.5,
          px: 1.25,
          py: 0.5,
        }}
      >
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor: "#ff6b9d",
            animation: "liveDot 1.2s ease-in-out infinite",
            "@keyframes liveDot": {
              "0%,100%": { opacity: 1 },
              "50%":     { opacity: 0.25 },
            },
          }}
        />
        <Typography sx={{ color: "#ff6b9d", fontSize: "0.7rem", fontWeight: 800, letterSpacing: 1 }}>
          LIVE
        </Typography>
      </Box>
    </Box>
  );
}

export default function StageLinkLiveStream({
  userId,
  videoId,
  onCallEnd,
  displayName,
  username,
  qrCodeClickHandler,
  bookingId,
  zg,
}: {
  userId: string;
  videoId: string;
  onCallEnd: () => void;
  displayName: string;
  username?: string;
  qrCodeClickHandler?: () => void;
  bookingId: string;
  zg: ZegoExpressEngine | null;
}) {
  const [audienceCameraOn, setAudienceCameraOn] = useState(false);
  const [videoToken, setVideoToken] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [karaokeActive, setKaraokeActive] = useState(false);
  const [karaokeTrackName, setKaraokeTrackName] = useState("");
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "info" | "success";
  }>({ open: false, message: "", severity: "info" });

  // Karaoke playback progress (driven by RAF loop for smooth updates)
  const [karaokeCurrentTime, setKaraokeCurrentTime] = useState(0);
  const [karaokeDuration, setKaraokeDuration]       = useState(0);

  // Hidden <Audio> element that syncs with the talent's karaoke broadcast
  const karaokeAudioRef    = useRef<HTMLAudioElement | null>(null);
  const karaokeRafRef      = useRef<number | null>(null);

  const startKaraokeProgress = () => {
    if (karaokeRafRef.current) return;
    const tick = () => {
      const a = karaokeAudioRef.current;
      if (a) {
        setKaraokeCurrentTime(a.currentTime);
        if (a.duration && isFinite(a.duration)) setKaraokeDuration(a.duration);
      }
      karaokeRafRef.current = requestAnimationFrame(tick);
    };
    karaokeRafRef.current = requestAnimationFrame(tick);
  };

  const stopKaraokeProgress = () => {
    if (karaokeRafRef.current) { cancelAnimationFrame(karaokeRafRef.current); karaokeRafRef.current = null; }
  };

  // PLCV Video states
  const [isVideoMode, setIsVideoMode] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const videoPlayerRef = useRef<any>(null);

  // Zego specific states
  const [audienceStream, setAudienceStream] = useState<MediaStream | null>(null);
  const [isJoined, setIsJoined] = useState(false);
  const [remoteStreams, setRemoteStreams] = useState<Map<string, MediaStream>>(new Map()); // Changed to support multiple streams
  const [remoteStreamInfo, setRemoteStreamInfo] = useState<Map<string, { userName: string; streamID: string }>>(new Map());
 
  console.log("remoteStreams zg ", zg);
  console.log("remoteStreams zg streams", remoteStreams);
  console.log("remoteStreams zg streamInfo", remoteStreamInfo);

  // Video refs
  const audienceVideoRef = useRef<HTMLVideoElement>(null);
  const fullscreenRef    = useRef<HTMLDivElement>(null);

  const [showEndDialog, setShowEndDialog] = useState(false);

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const btnSize = isMobile ? 36 : 42;
  const controlButtonStyle = {
    height: btnSize,
    width: btnSize,
    padding: isMobile ? "6px" : "8px",
    backgroundColor: "rgba(238,242,255,0.92)",
    color: "#1a1a1a",
    borderRadius: "50%",
    zIndex: 3,
    backdropFilter: "blur(4px)",
  };

  const controlButtonStyleDestructive = {
    height: btnSize,
    width: btnSize,
    padding: isMobile ? "6px" : "8px",
    backgroundColor: "#E50914",
    color: "#fff",
    borderRadius: "50%",
    zIndex: 3,
  };

  // Safe video play function to prevent interruption errors
  const safeVideoPlay = async (videoElement: HTMLVideoElement): Promise<void> => {
    try {
      if (!videoElement.paused) return;
      if (videoElement.readyState < 1) {
        await new Promise<void>((resolve) => {
          const onLoadedMetadata = () => {
            videoElement.removeEventListener("loadedmetadata", onLoadedMetadata);
            resolve();
          };
          videoElement.addEventListener("loadedmetadata", onLoadedMetadata);
        });
      }
      await videoElement.play();
    } catch (error: any) {
      if (error?.name !== "AbortError") {
        console.error("Video play error:", error);
      }
    }
  };

  // Handle broadcast messages for video control
  useEffect(() => {
    if (!zg) return;

    const handleBroadcastMessage = (roomID: string, chatData: any[]) => {
      chatData.forEach((data) => {
        try {
          const message = JSON.parse(data.message);

          if (message.type === "karaoke_status") {
            const active: boolean = message.data?.active ?? false;
            const trackName: string | undefined = message.data?.trackName;
            setKaraokeActive(active);
            if (trackName) setKaraokeTrackName(trackName);
            if (!active) setKaraokeTrackName("");
            setSnackbar({
              open: true,
              message: active
                ? "🎤 Talent started Karaoke Mix — instrumental + mic now live"
                : "🎤 Karaoke Mix ended — back to normal stream",
              severity: active ? "info" : "success",
            });
          }

          if (message.type === "karaoke_audio") {
            const { action, url, trackName: msgTrackName, currentTime: ct, duration: dur, isPlaying } = message.data ?? {};

            switch (action) {
              case "load": {
                stopKaraokeProgress();
                if (karaokeAudioRef.current) {
                  karaokeAudioRef.current.pause();
                  karaokeAudioRef.current.src = "";
                }
                const audio = new Audio(url);
                audio.preload = "auto";
                audio.addEventListener("loadedmetadata", () => {
                  if (audio.duration && isFinite(audio.duration)) setKaraokeDuration(audio.duration);
                });
                audio.onended = () => {
                  stopKaraokeProgress();
                  setKaraokeActive(false);
                  setKaraokeTrackName("");
                  setKaraokeCurrentTime(0);
                  setKaraokeDuration(0);
                };
                karaokeAudioRef.current = audio;
                setKaraokeTrackName(msgTrackName ?? "Karaoke Track");
                setKaraokeCurrentTime(0);
                break;
              }

              case "play": {
                const audio = karaokeAudioRef.current;
                if (!audio) break;
                if (msgTrackName) setKaraokeTrackName(msgTrackName);
                // Always snap to the talent's exact position — no tolerance window
                if (ct !== undefined) audio.currentTime = ct;
                const doPlay = () => audio.play().catch(() => {});
                // Defer play until the audio buffer is ready
                if (audio.readyState >= 2) {
                  doPlay();
                } else {
                  audio.addEventListener("canplay", doPlay, { once: true });
                }
                startKaraokeProgress();
                setKaraokeActive(true);
                setSnackbar({
                  open: true,
                  message: `🎤 Karaoke started — "${msgTrackName ?? karaokeTrackName}" is live!`,
                  severity: "info",
                });
                break;
              }

              case "pause": {
                const audio = karaokeAudioRef.current;
                if (audio) {
                  audio.pause();
                  // Always snap to the paused position the talent reported
                  if (ct !== undefined) audio.currentTime = ct;
                }
                stopKaraokeProgress();
                break;
              }

              case "seek": {
                const audio = karaokeAudioRef.current;
                if (!audio || ct === undefined) break;
                const applySeek = () => {
                  audio.currentTime = ct;
                  if (isPlaying) audio.play().catch(() => {});
                  else audio.pause();
                };
                // If the buffer isn't ready yet, defer until it is
                if (audio.readyState >= 2) {
                  applySeek();
                } else {
                  audio.addEventListener("canplay", applySeek, { once: true });
                }
                break;
              }

              case "sync": {
                const audio = karaokeAudioRef.current;
                if (!audio || ct === undefined) break;
                // Correct any drift larger than 0.5 s — tight enough to feel in-sync
                if (Math.abs(audio.currentTime - ct) > 0.5) audio.currentTime = ct;
                if (isPlaying && audio.paused)   audio.play().catch(() => {});
                if (!isPlaying && !audio.paused) audio.pause();
                break;
              }

              case "stop":
              case "end": {
                const audio = karaokeAudioRef.current;
                if (audio) { audio.pause(); audio.currentTime = 0; }
                stopKaraokeProgress();
                setKaraokeActive(false);
                setKaraokeTrackName("");
                setKaraokeCurrentTime(0);
                setKaraokeDuration(0);
                setSnackbar({
                  open: true,
                  message: action === "end" ? "🎤 Karaoke track finished!" : "🎤 Karaoke ended",
                  severity: "success",
                });
                break;
              }
              default:
                break;
            }
          }

          if (message.type === "player_control") {
            const {
              action,
              videoUrl: msgVideoUrl,
              currentTime: msgCurrentTime,
              isPlaying,
            } = message.data;

            switch (action) {
              case "start":
                setVideoUrl(msgVideoUrl);
                setIsVideoMode(true);
                setIsVideoPlaying(false);
                break;
              case "play":
                setIsVideoPlaying(true);
                if (videoPlayerRef.current) {
                  videoPlayerRef.current.currentTime = msgCurrentTime;
                }
                break;
              case "pause":
                setIsVideoPlaying(false);
                if (videoPlayerRef.current) {
                  videoPlayerRef.current.currentTime = msgCurrentTime;
                }
                break;
              case "seek":
                if (videoPlayerRef.current) {
                  videoPlayerRef.current.currentTime = msgCurrentTime;
                }
                setIsVideoPlaying(isPlaying);
                break;
              case "end":
              case "stop":
                setIsVideoMode(false);
                setIsVideoPlaying(false);
                setVideoUrl("");
                setCurrentTime(0);
                break;
            }
          }
        } catch (error) {
          console.error("Error parsing broadcast message:", error);
        }
      });
    };

    zg.on("IMRecvBroadcastMessage", handleBroadcastMessage);

    return () => {
      zg.off("IMRecvBroadcastMessage", handleBroadcastMessage);
    };
  }, [zg]);

  // Video player event handlers
  const handleVideoProgress = (playedSeconds: number) => {
    setCurrentTime(playedSeconds);
  };

  const handleVideoTimeUpdate = () => {
    const video = videoPlayerRef.current;
    if (video) {
      setCurrentTime(video.currentTime);
      setDuration(video.duration);
    }
  };

  const handleVideoLoadedMetadata = () => {
    const video = videoPlayerRef.current;
    if (video) {
      setDuration(video.duration);
    }
  };

  const handleTimeLine = (e: any, value: number | number[]) => {
    const seekVal = Array.isArray(value) ? value[0] : value;
    if (videoPlayerRef.current) {
      videoPlayerRef.current.currentTime = (seekVal / 100) * (duration || 0);
    }
  };

  const handleVideoSeek = () => {
    // This will be handled by the host's VideoController
  };

  // Always fetch a fresh token — cached tokens can be expired and cause
  // silent auth failures with Zego (same fix applied to TalentLiveStream).
  useEffect(() => {
    if (videoToken) return;
    getZegoToken(userId!)
      .then((token: string) => {
        console.log("[StageLinkLiveStream] Fresh Zego token acquired");
        setVideoToken(token);
      })
      .catch((err: any) => {
        console.error("[StageLinkLiveStream] Token fetch failed", err.message);
        setError(err.message || "Failed to fetch token");
      });
  }, [videoId, userId]);

  // Join room and setup streams
  const joinRoom = async () => {
    try {
      if (isJoined || !zg || !videoToken) return;

      // Login to room as audience
      await zg.loginRoom(
        videoId,
        videoToken,
        { userID: userId, userName: displayName || userId },
        { userUpdate: true }
      );

      console.log(`[Zego] Room login success as Audience`);
      setIsJoined(true);
    } catch (error) {
      console.error("Join room error:", error);
      setError("Failed to join room");
    }
  };

  // Setup remote stream listeners - Updated to handle multiple streams
  useEffect(() => {
    if (!zg) return;

    const handleRoomStreamUpdate = async (
      roomID: string,
      updateType: string,
      streamList: any[]
    ) => {
      console.log("Stream update:", updateType, streamList);
      
      if (updateType === "ADD") {
        try {
          // Handle multiple streams
          for (const streamInfo of streamList) {
            const stream = await zg.startPlayingStream(streamInfo.streamID);
            
            if (stream) {
              setRemoteStreams(prev => new Map(prev.set(streamInfo.streamID, stream)));
              setRemoteStreamInfo(prev => new Map(prev.set(streamInfo.streamID, {
                userName: streamInfo.userName || `User ${streamInfo.streamID}`,
                streamID: streamInfo.streamID
              })));
              console.log(`Remote stream added: ${streamInfo.streamID}`);
            }
          }
        } catch (error) {
          console.error("Error playing remote stream:", error);
        }
      } else if (updateType === "DELETE") {
        // Remove streams when users leave
        for (const streamInfo of streamList) {
          setRemoteStreams(prev => {
            const newMap = new Map(prev);
            newMap.delete(streamInfo.streamID);
            return newMap;
          });
          setRemoteStreamInfo(prev => {
            const newMap = new Map(prev);
            newMap.delete(streamInfo.streamID);
            return newMap;
          });
          zg.stopPlayingStream(streamInfo.streamID);
          console.log(`Remote stream removed: ${streamInfo.streamID}`);
        }
      }
    };

    zg.on("roomStreamUpdate", handleRoomStreamUpdate);

    return () => {
      zg.off("roomStreamUpdate", handleRoomStreamUpdate);
    };
  }, [zg]);

  // Create audience stream when camera is turned on
  const createAudienceStream = async () => {
    try {
      if (!zg || !isJoined || !audienceCameraOn) return;

      const stream = await zg.createStream({
        camera: { audio: false, video: true }, // Audience only publishes video
      });

      if (audienceVideoRef.current && stream) {
        audienceVideoRef.current.srcObject = stream;
        audienceVideoRef.current.muted = true;
        await safeVideoPlay(audienceVideoRef.current);
        await zg.startPublishingStream("audience_stream_" + userId, stream);
        setAudienceStream(stream);
      }
    } catch (error) {
      console.error("Error creating audience stream:", error);
    }
  };

  // Stop audience stream
  const stopAudienceStream = () => {
    if (audienceStream && zg) {
      audienceStream.getTracks().forEach((track) => track.stop());
      zg.stopPublishingStream("audience_stream_" + userId);
      setAudienceStream(null);
      if (audienceVideoRef.current) {
        audienceVideoRef.current.srcObject = null;
      }
    }
  };

  // Cleanup function
  const cleanup = () => {
    // Stop karaoke audio + RAF
    stopKaraokeProgress();
    if (karaokeAudioRef.current) {
      karaokeAudioRef.current.pause();
      karaokeAudioRef.current.src = "";
      karaokeAudioRef.current = null;
    }
    if (zg && isJoined) {
      if (audienceStream) {
        audienceStream.getTracks().forEach((track) => track.stop());
        zg.stopPublishingStream("audience_stream_" + userId);
      }

      // Stop remote stream
      if (remoteStreams.size > 0) {
        for (const stream of remoteStreams.values()) {
          stream.getTracks().forEach((track) => track.stop());
        }
        for (const streamInfo of remoteStreamInfo.values()) {
          zg.stopPlayingStream(streamInfo.streamID);
        }
      }

      zg.logoutRoom(videoId);
      setIsJoined(false);
      setAudienceStream(null);
      setRemoteStreams(new Map());
      setRemoteStreamInfo(new Map());
    }
  };

  // Join room when dependencies are ready
  useEffect(() => {
    if (zg && videoToken) joinRoom();

    return () => {
      cleanup();
    };
    // eslint-disable-next-line
  }, [zg, videoToken]);

  // Handle audience camera toggle
  useEffect(() => {
    if (isJoined) {
      if (audienceCameraOn) {
        createAudienceStream();
      } else {
        stopAudienceStream();
      }
    }
    // eslint-disable-next-line
  }, [audienceCameraOn, isJoined]);

  // Fullscreen event listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = () => {
    if (isFullscreen) {
      document.exitFullscreen().catch(() => {});
    } else {
      fullscreenRef.current?.requestFullscreen().catch(() => {});
    }
  };

  const toggleAudienceCamera = () => {
    setAudienceCameraOn((prev) => !prev);
  };

  const handleEndCallClick = () => setShowEndDialog(true);

  const handleEndCall = () => {
    cleanup();
    onCallEnd();
    setShowEndDialog(false);
  };

  // Render video content based on mode
  const renderVideoContent = () => {
    if (isVideoMode && videoUrl) {
      // Show PLCV Video Player
      return (
        <>
          <PLCVVideoPlayer
            playerRef={videoPlayerRef}
            url={videoUrl}
            isHost={false} // Audience view
            isLarge={true}
            playing={isVideoPlaying}
            handleVideoProgress={handleVideoProgress}
            handleVideoSeek={handleVideoSeek}
            handleTimeLine={handleTimeLine}
            handleVideoTimeUpdate={handleVideoTimeUpdate}
            playbackRate={1}
            currentTime={currentTime}
            duration={duration}
            showProgressiveBar={false}
          />

          {/* Audience's own video in small size during video mode */}
          {audienceCameraOn && (
            <Box
              sx={{
                position: "absolute",
                bottom: "100px",
                right: "20px",
                width: "200px",
                height: "150px",
                borderRadius: "8px",
                overflow: "hidden",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              }}
            >
              <video
                ref={audienceVideoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  backgroundColor: "#000",
                }}
              />
            </Box>
          )}

          {/* Poll Display during video mode */}
          <AudiencePollDisplay bookingId={bookingId ?? ""} />
        </>
      );
    }

    // Show live video call - Updated to use MultiStreamGrid
    if (remoteStreams.size > 0) {
      return (
        <>
          {/* Multiple remote streams in grid layout */}
          <MultiStreamGrid 
            streams={remoteStreams}
            streamInfo={remoteStreamInfo}
            isFullscreen={isFullscreen}
          />

          {/* Audience's own video in small size */}
          {audienceCameraOn && (
            <Box
              sx={{
                position: "absolute",
                bottom: "100px",
                right: "20px",
                width: "200px",
                height: "150px",
                borderRadius: "8px",
                overflow: "hidden",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              }}
            >
              <video
                ref={audienceVideoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  backgroundColor: "#000",
                }}
              />
            </Box>
          )}

          {/* Poll Display for Audience */}
          <AudiencePollDisplay bookingId={bookingId ?? ""} />
        </>
      );
    }

    // Waiting state
    return (
      <Box
        style={{
          height: isFullscreen ? "100vh" : "100%",
          width: "100%",
          backgroundColor: "#000",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="h6"
          align="center"
          style={{
            color: "white",
          }}
        >
          {isVideoMode ? "Loading video..." : "Waiting for host to join..."}
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <MainContainer ref={fullscreenRef}>
        <VideoCallWrapper
          sx={{
            position: "relative",
            height: isFullscreen ? "100vh" : "100%",
            width: "100%",
            bgcolor: "#000",
            opacity: 1,
            transition: "all 0.3s ease-in-out",
          }}
        >
          {/* Main Video Section */}
          <Box sx={{
            width: "100%",
            height: isFullscreen ? "100vh" : "100%",
            position: "relative",
            borderRadius: isFullscreen ? 0 : "8px",
            overflow: "hidden",
            // Neon karaoke border — pulses while active
            ...(karaokeActive && {
              outline: "2px solid #ff6b9d",
              boxShadow: "0 0 0 3px rgba(255,107,157,0.25), 0 0 28px 6px rgba(255,107,157,0.18)",
              animation: "karaokeBorder 2.4s ease-in-out infinite",
              "@keyframes karaokeBorder": {
                "0%,100%": { boxShadow: "0 0 0 3px rgba(255,107,157,0.25), 0 0 28px 6px rgba(255,107,157,0.18)" },
                "50%":     { boxShadow: "0 0 0 4px rgba(34,211,238,0.35),  0 0 42px 10px rgba(34,211,238,0.22)" },
              },
            }),
            transition: "outline 0.5s ease, box-shadow 0.5s ease",
          }}>
            {renderVideoContent()}

            {/* Full karaoke overlay — shown when active */}
            {karaokeActive && (
              <KaraokeOverlay
                trackName={karaokeTrackName}
                currentTime={karaokeCurrentTime}
                duration={karaokeDuration}
              />
            )}
          </Box>

          {/* ── Control bar — centered glassmorphism pill ── */}
          <Box
            sx={{
              position: "absolute",
              bottom: { xs: 12, sm: 18 },
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1000,
              display: "inline-flex",
              alignItems: "center",
              gap: { xs: 0.5, sm: 0.75 },
              px: { xs: 1.25, sm: 1.75 },
              py: { xs: 0.75, sm: 1 },
              borderRadius: "999px",
              bgcolor: "rgba(10,10,10,0.75)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255,255,255,0.13)",
              boxShadow: "0 6px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05) inset",
              whiteSpace: "nowrap",
            }}
          >
            {/* ── Camera ── */}
            <Tooltip title={audienceCameraOn ? "Turn off camera" : "Turn on camera"} placement="top" arrow>
              <Box component="span">
                <ControlButton
                  onClick={toggleAudienceCamera}
                  icon={audienceCameraOn ? <Videocam sx={{ fontSize: 18 }} /> : <VideocamOff sx={{ fontSize: 18 }} />}
                  sx={{
                    ...controlButtonStyle,
                    backgroundColor: audienceCameraOn ? "rgba(238,242,255,0.92)" : "rgba(229,9,20,0.85)",
                    color: audienceCameraOn ? "#1a1a1a" : "#fff",
                    "&:hover": { backgroundColor: audienceCameraOn ? "#fff" : "#b20710" },
                    transition: "background-color 0.2s",
                  }}
                />
              </Box>
            </Tooltip>

            {/* ── Thin separator ── */}
            <Box sx={{ width: "1px", height: 22, bgcolor: "rgba(255,255,255,0.18)", mx: 0.5, flexShrink: 0 }} />

            {/* ── Fullscreen ── */}
            <Tooltip title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"} placement="top" arrow>
              <Box component="span">
                <ControlButton
                  onClick={toggleFullscreen}
                  icon={isFullscreen
                    ? <FullscreenExit sx={{ fontSize: 18 }} />
                    : <Fullscreen sx={{ fontSize: 18 }} />
                  }
                  sx={{
                    ...controlButtonStyle,
                    "&:hover": { backgroundColor: "#fff" },
                  }}
                />
              </Box>
            </Tooltip>

            {/* ── QR (optional) ── */}
            {qrCodeClickHandler && (
              <Tooltip title="Show QR code" placement="top" arrow>
                <Box component="span">
                  <ControlButton
                    onClick={qrCodeClickHandler}
                    icon={<QrCode2 sx={{ fontSize: 18 }} />}
                    sx={{
                      ...controlButtonStyle,
                      "&:hover": { backgroundColor: "#fff" },
                    }}
                  />
                </Box>
              </Tooltip>
            )}

            {/* ── Thin separator ── */}
            <Box sx={{ width: "1px", height: 22, bgcolor: "rgba(255,255,255,0.18)", mx: 0.5, flexShrink: 0 }} />

            {/* ── Leave session ── */}
            <Tooltip title="Leave session" placement="top" arrow>
              <Box component="span">
                <ControlButton
                  onClick={handleEndCallClick}
                  icon={<CallEnd sx={{ fontSize: 18 }} />}
                  sx={{
                    ...controlButtonStyleDestructive,
                    "&:hover": { backgroundColor: "#b20710" },
                    transition: "background-color 0.2s",
                  }}
                />
              </Box>
            </Tooltip>
          </Box>

          {/* ── Leave Session Dialog ── */}
          <Dialog
            open={showEndDialog}
            onClose={() => setShowEndDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              <Typography variant="h6" component="div">Leave Session</Typography>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Are you sure you want to leave the live stream?
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
              <Button onClick={() => setShowEndDialog(false)} variant="outlined" color="primary">
                Cancel
              </Button>
              <Button onClick={handleEndCall} variant="contained" color="error">
                Leave
              </Button>
            </DialogActions>
          </Dialog>
        </VideoCallWrapper>
      </MainContainer>

      {/* Karaoke start/stop toast notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          sx={{ fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

const ControlButton = ({
  onClick,
  icon,
  sx = {},
}: {
  onClick: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  icon: ReactNode;
  sx: Record<string, any>;
}) => (
  <IconButton color="error" onClick={onClick} sx={{ ...sx }}>
    {icon}
  </IconButton>
);

// Component to display multiple remote streams in a grid
const MultiStreamGrid = ({ 
  streams, 
  streamInfo, 
  isFullscreen 
}: { 
  streams: Map<string, MediaStream>;
  streamInfo: Map<string, { userName: string; streamID: string }>;
  isFullscreen: boolean;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  
  const streamArray = Array.from(streams.entries());
  
  // Determine grid layout based on number of streams and screen size
  const getGridLayout = () => {
    const count = streamArray.length;
    
    if (isMobile) {
      return count === 1 ? "1fr" : count === 2 ? "1fr 1fr" : "1fr 1fr";
    }
    
    if (isTablet) {
      if (count === 1) return "1fr";
      if (count === 2) return "1fr 1fr";
      if (count === 3) return "1fr 1fr";
      return "1fr 1fr";
    }
    
    // Desktop layout
    if (count === 1) return "1fr";
    if (count === 2) return "1fr 1fr";
    if (count === 3) return "1fr 1fr 1fr";
    if (count === 4) return "1fr 1fr";
    return "1fr 1fr 1fr";
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: getGridLayout(),
        gap: 2,
        width: "100%",
        height: "100%",
        p: isFullscreen ? 0 : 2,
      }}
    >
      {streamArray.map(([streamID, stream]) => {
        const info = streamInfo.get(streamID);
        return (
          <StreamVideo
            key={streamID}
            stream={stream}
            streamID={streamID}
            userName={info?.userName || `User ${streamID}`}
            isMobile={isMobile}
          />
        );
      })}
    </Box>
  );
};

// Individual stream video component
const StreamVideo = ({ 
  stream, 
  streamID, 
  userName, 
  isMobile 
}: { 
  stream: MediaStream;
  streamID: string;
  userName: string;
  isMobile: boolean;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  
  // Apply stream to video element
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(console.error);
    }
  }, [stream, streamID]);
  
  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: 2,
        overflow: "hidden",
        backgroundColor: "#000",
        minHeight: isMobile ? "200px" : "300px",
      }}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          backgroundColor: "#000",
        }}
      />
    </Box>
  );
};
